// Portal outcome states, ported from Buy Flow.dc.html: the Quote Pending
// holding state (addendum §5: price locked, button disabled with a spinner,
// the two-minute fallback line), underwriter review, declined, expired,
// returning, the payment interstitial, and the confirmation. Every string is
// verbatim from the comp with the school's name substituted, except where the
// comp promised an email this site never sends (2026-11-05: Global sends its
// own; clearedto.rent sends the student nothing).
// Wired 2026-11-05 (CC-PROMPT-CTR-GLOBAL-CHECKOUT): the interstitial's button
// opens Global's summary link for THIS quote (lib/useQuoteHandoff.js); every
// other state is the server's derived state (utils/ctr-renter-state.js), never
// derived here. 'binding' and 'status' are the two server states the comp had
// no screen for; both reuse its parts.
import { useEffect, useState } from 'react';
import Spinner from '@/components/ui/Spinner.jsx';

const HANDOFF_ERRORS = {
  requote_failed: "We couldn't price your changes with Global just now, so there is no payment page for them yet. Go back to your coverage and try again in a minute.",
  acks_required: 'Your coverage changed after you confirmed the acknowledgements. Go back to your coverage and confirm them for this coverage.',
  too_many_changes: 'This quote has been changed too many times to change again here. Go back to your coverage to buy it as it stands, or start a new quote.',
  below_school_floor: "That coverage is below your school's requirement. Go back to your coverage and choose again.",
};

function Terminal({ icon, iconClass, title, children }) {
  return (
    <div className="my-auto py-[30px] text-center">
      <div className={`mx-auto mb-5 flex h-[58px] w-[58px] items-center justify-center rounded-modal text-2xl ${iconClass}`}>{icon}</div>
      <h1 className="mb-2.5 text-[22px] font-bold leading-[1.25] tracking-[-0.02em]">{title}</h1>
      {children}
    </div>
  );
}

/** The §5 holding block: rendered inside step 3's sticky bar while the quote
 *  sits in Global's Quote Pending. Exported for the price step (T8). */
export function FinalizingBlock({ afterPayment = false }) {
  const [fallback, setFallback] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 120000);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <button type="button" disabled
        className="flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-card-lg bg-fill text-[15px] font-bold text-ink-dim">
        <Spinner />
        {afterPayment ? 'Global is issuing your binder' : 'Finalizing with the carrier, usually under a minute'}
      </button>
      <div className="mt-2 text-center text-[11.5px] leading-[1.5] text-ink-faint">
        {afterPayment
          ? 'This page updates by itself, and it keeps your place: come back to this same address any time.'
          : fallback
            ? 'Global Aerospace also emails you your payment link. You can close this page and go fly.'
            : 'Your price is locked. If this takes longer than a couple of minutes, Global Aerospace also emails you your payment link.'}
      </div>
    </>
  );
}

export default function Outcome({ outcome, school, email, onRestart, onContinuePayment, onBackToCoverage, summaryUrl = null, watching = false, handoffError = null, statusLabel = null, effectiveDate = null }) {
  // The direct flow may have NO school (attribution skipped): every school
  // line below renders only when there is one to talk about.
  const sn = school?.name || null;
  if (outcome === 'interstitial') {
    return (
      <Terminal
        icon={(
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-[26px] w-[26px]">
            <rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
          </svg>
        )}
        iconClass="bg-teal-fill text-accent"
        title="You're going to Global Aerospace's secure payment page"
      >
        <p className="mx-auto mb-2 max-w-[360px] text-sm leading-[1.6] text-ink-dim">
          {sn
            ? <>Once Global confirms your payment and issues your binder, {sn} sees you as cleared; Global&apos;s certificate follows, automatically.</>
            : <>Once Global confirms your payment, your binder is on file; Global&apos;s certificate follows, automatically.</>}
        </p>
        <p className="mx-auto mb-[26px] max-w-[360px] text-xs leading-[1.6] text-ink-faint">
          Global collects your street address and payment there. Coverage by Global Aerospace, arranged by NDEMNIFLY Aviation Insurance.
        </p>
        {handoffError ? (
          <p className="mx-auto mb-4 max-w-[360px] rounded-card bg-warn-bg p-[10px_12px] text-[13px] leading-[1.5] text-warn-deep">
            {HANDOFF_ERRORS[handoffError] || HANDOFF_ERRORS.requote_failed}
          </p>
        ) : summaryUrl ? (
          <>
            <a href={summaryUrl} target="_blank" rel="noopener noreferrer" onClick={onContinuePayment}
              className="inline-block min-h-[52px] rounded-card-lg bg-accent px-7 py-[15px] text-[15px] font-bold text-white hover:bg-accent-hover">
              Continue to payment ↗
            </a>
            <p className="mx-auto mt-3 max-w-[360px] text-xs leading-[1.6] text-ink-faint">
              {watching
                ? 'Once you have paid, this page updates by itself. You can also come back to this same address later.'
                : 'Global Aerospace also emails you this same link.'}
            </p>
          </>
        ) : (
          <p className="mx-auto mb-2 max-w-[360px] rounded-card border border-line bg-white p-[12px_14px] text-[13px] leading-[1.55] text-ink-soft">
            Global Aerospace emails you the link to its secure payment page. Open it from that email to finish.
          </p>
        )}
        <div className="mt-3.5">
          <button type="button" onClick={onBackToCoverage} className="text-[13px] font-semibold text-ink-dim">← Back to my coverage</button>
        </div>
      </Terminal>
    );
  }

  if (outcome === 'review') {
    return (
      <Terminal icon="◔" iconClass="bg-warn-bg text-warn-fg" title="An underwriter needs a closer look">
        <p className="mx-auto mb-2 max-w-[370px] text-sm leading-[1.6] text-ink-dim">
          No instant price this time, and that&apos;s normal for some applications. You&apos;ll have your quote by
          email, usually within one business day.
        </p>
        {sn ? (
          <p className="mx-auto mb-[26px] max-w-[370px] text-[13px] leading-[1.6] text-ink-dim">
            <b className="text-ink-soft">{sn} has been told you&apos;ve applied</b>, so nobody at the counter is wondering.
          </p>
        ) : <div className="mb-[26px]" />}
        <div className="mx-auto max-w-[370px] rounded-card-lg border border-line bg-white p-[16px_18px] text-left text-[12.5px] leading-[1.6] text-ink-dim">
          Nothing else to do right now. Global Aerospace writes to <b className="text-ink-soft">{email}</b> about
          your application, and this page&apos;s address shows your quote&apos;s latest status whenever you come back.
        </div>
      </Terminal>
    );
  }

  if (outcome === 'declined') {
    return (
      <Terminal icon="☎" iconClass="bg-fill text-ink-dim" title="We couldn't offer coverage automatically">
        <p className="mx-auto mb-2 max-w-[380px] text-sm leading-[1.6] text-ink-dim">
          Some pilots rate differently with other carriers we work with. A licensed producer at NDEMNIFLY will
          place your coverage another way; pick a time that suits you.
        </p>
        {sn ? (
          <p className="mx-auto mb-[22px] max-w-[380px] text-[13px] leading-[1.6] text-ink-dim">
            <b className="text-ink-soft">{sn} has been told your coverage is being placed another way</b>, so nobody at the counter is wondering.
          </p>
        ) : <div className="mb-[22px]" />}
        <a href="mailto:support@ndemnifly.com?subject=clearedto.rent%20coverage"
          className="inline-block min-h-[52px] rounded-card-lg bg-primary px-[26px] py-[15px] text-[15px] font-bold text-white hover:bg-primary-deep">
          Pick a time with a producer 📅
        </a>
        <p className="mx-auto mt-3.5 max-w-[380px] text-[11px] leading-[1.6] text-ink-faint">
          Or they&apos;ll call you within one business day. Coverage by Global Aerospace, arranged by NDEMNIFLY
          Aviation Insurance, CA License #6018700.
        </p>
      </Terminal>
    );
  }

  if (outcome === 'expired') {
    return (
      <Terminal icon="⏱" iconClass="bg-fill text-ink-dim" title="That quote has expired">
        <p className="mx-auto mb-[26px] max-w-[360px] text-sm leading-[1.6] text-ink-dim">
          Quotes are good for 30 days and yours has aged out. A fresh quote takes a couple of minutes, and prices
          rarely move much.
        </p>
        <button type="button" onClick={onRestart}
          className="min-h-[52px] rounded-card-lg bg-primary px-7 text-[15px] font-bold text-white hover:bg-primary-deep">
          Refresh my quote
        </button>
      </Terminal>
    );
  }

  if (outcome === 'confirmation') {
    return (
      <>
        <div className="pb-2.5 pt-[26px] text-center">
          <div className="mx-auto mb-[18px] flex h-16 w-16 items-center justify-center rounded-pill bg-ok-bg text-[28px] text-ok-fg">✓</div>
          <h1 className="mb-1.5 text-2xl font-bold tracking-[-0.02em]">{sn ? <>You&apos;re cleared to rent at {sn}</> : <>You&apos;re cleared to rent</>}</h1>
          <div className="mb-6 text-[13px] text-ink-dim">Your binder is on file{effectiveDate ? <> · effective {effectiveDate}</> : null}</div>
        </div>
        <div className="mb-3.5 rounded-panel border border-line bg-white p-5">
          <div className="mb-3 text-[13px] font-bold">What happens next</div>
          <div className="flex flex-col gap-3 text-[13px] leading-[1.5] text-ink-soft">
            {sn ? <div className="flex gap-2.5"><span className="flex-none font-bold text-ok-fg">✓</span><span>{sn}&apos;s dashboard already shows you as cleared, binder on file. The front desk can see it right now.</span></div> : null}
            <div className="flex gap-2.5"><span className="flex-none font-bold text-ok-fg">✓</span><span>Global is issuing your certificate of insurance{sn ? <>; it lands on {sn}&apos;s dashboard automatically, usually within minutes</> : ' and emailing it to you'}.</span></div>
            <div className="flex gap-2.5"><span className="flex-none font-bold text-ok-fg">✓</span><span>Your policy documents come from Global Aerospace.</span></div>
          </div>
        </div>
        <div className="mb-3.5 rounded-card-lg border border-teal-line bg-teal-card p-[15px_17px]">
          <div className="mb-1 text-[13px] font-bold">Fly at more than one school?</div>
          <div className="mb-2.5 text-[12.5px] leading-[1.55] text-ink-dim">
            One policy can clear you everywhere. Add another school on clearedto.rent and your certificate
            appears on their dashboard too, kept current automatically.
          </div>
        </div>
      </>
    );
  }

  if (outcome === 'binding') {
    return (
      <div className="my-auto py-[30px]">
        <div className="mb-4 text-center text-sm text-ink-dim">Global has your bind order.</div>
        <FinalizingBlock afterPayment />
      </div>
    );
  }

  if (outcome === 'status') {
    return (
      <Terminal icon="ⓘ" iconClass="bg-fill text-ink-dim" title="Your quote with Global Aerospace">
        <p className="mx-auto mb-2 max-w-[360px] text-sm leading-[1.6] text-ink-dim">
          Global shows this quote as <b className="text-ink-soft">{statusLabel || 'not yet answered'}</b>.
        </p>
        <p className="mx-auto mb-[26px] max-w-[360px] text-xs leading-[1.6] text-ink-faint">
          Global Aerospace&apos;s emails to you have the latest on it. This page updates when Global does.
        </p>
      </Terminal>
    );
  }

  return null;
}
